import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { SectionTitle } from "./ui";

const ContactsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const contactInfo = [
    {
      icon: Phone,
      title: "Телефон",
      value: "+7 (778) 922-59-35",
      href: "tel:+77789225935",
      color: "from-primary-500 to-teal-500",
    },
    {
      icon: MapPin,
      title: "Главный офис",
      value: "г. Астана, ул. Сафуан Шаймерденов, 8",
      href: "https://2gis.kz/astana/geo/70030076151014572",
      color: "from-primary-600 to-teal-600",
    },
    {
      icon: MapPin,
      title: "Филиал Караганда",
      value: "г. Караганда, ул. Керамическая, 4/6",
      href: "https://2gis.kz/karaganda/search/Караганда%2C%20улица%20Керамическая%2C%204%2F6",
      color: "from-teal-500 to-primary-600",
    },

    {
      icon: Clock,
      title: "Режим работы",
      value: "Пн-Пт: 10:00 - 18:00",
      href: null,
      color: "from-teal-600 to-primary-600",
    },
  ];

  return (
    <section
      ref={ref}
      id="contacts"
      className="relative py-20 md:py-28 bg-gradient-to-br from-dark-900 via-dark-900 to-primary-950 overflow-hidden scroll-mt-24"
    >
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-primary-500/10 to-teal-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-teal-500/10 to-primary-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvZz48L3N2Zz4=')] opacity-40" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          subtitle="Свяжитесь с нами"
          title="Контакты"
          description="Мы всегда рады ответить на ваши вопросы"
          light
          className="mb-16"
        />

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info Cards */}
          <div className="space-y-4">
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {item.href ? (
                  <a
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="group block"
                  >
                    <ContactCard item={item} />
                  </a>
                ) : (
                  <ContactCard item={item} />
                )}
              </motion.div>
            ))}

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-primary-500/10 to-teal-500/10 border border-white/10"
            >
              <h3 className="text-lg font-semibold text-white mb-2">
                Остались вопросы?
              </h3>
              <p className="text-dark-300 mb-4">
                Напишите нам и мы ответим в ближайшее время
              </p>
              <motion.a
                href="mailto:zhasylzhelken@gmail.com"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-teal-500 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/30"
              >
                <Send className="w-4 h-4" />
                Написать письмо
              </motion.a>
            </motion.div>
          </div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 to-teal-500/20 rounded-3xl blur-xl" />
            <div className="relative h-[500px] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <iframe
                src="https://yandex.kz/map-widget/v1/?ll=71.436893%2C51.112030&z=17&text=%D0%A1%D0%B0%D1%84%D1%83%D0%B0%D0%BD%20%D0%A8%D0%B0%D0%B9%D0%BC%D0%B5%D1%80%D0%B4%D0%B5%D0%BD%D0%BE%D0%B2%208%2C%20%D0%90%D1%81%D1%82%D0%B0%D0%BD%D0%B0"
                width="100%"
                height="100%"
                frameBorder="0"
                allowFullScreen
                title="Карта филиала"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

function ContactCard({ item }) {
  const Icon = item.icon;

  return (
    <div className="p-5 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 group-hover:translate-x-2">
      <div className="flex items-start gap-4">
        <div
          className={`w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-gradient-to-r ${item.color} shadow-lg`}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-dark-400 text-sm mb-1">{item.title}</p>
          <p className="text-white font-medium">{item.value}</p>
        </div>
      </div>
    </div>
  );
}

export default ContactsSection;
